// MapEditor（Phase 4.6）：替代老模板 mapTools factory 的业务逻辑。
//
// 这是一个受控编辑器：接收 map + 所属 continents + 提交回调。
// - 基础字段（map_id / name / continent_id / nested_group / sort_order / 复选项）
// - enter_steps_json / edges_json / match_json / fight_defaults_json：作为 JSON textarea
// - enterConditions：通过 ConditionBuilder 配置
// - 实时合法性提示：JSON 文本走 try-parse；非法则禁用提交
// - nested_group 同组高亮：传入 sameGroupMaps 时显示 ChipList 提醒

import { useEffect, useMemo, useState, type FormEvent } from "react";
import { z } from "zod";
import type {
  GameContinent,
  GameMap,
  GameMapSummary,
  MapUpsertInput,
} from "@/api/games";
import { Button } from "@/components/ui/Button";
import { Field, Input, Textarea } from "@/components/ui/Input";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { ConditionBuilder } from "@/components/condition-builder/ConditionBuilder";
import { ConditionSchema, type Condition } from "@/components/condition-builder/schema";

export interface MapEditorProps {
  initial: Partial<GameMap>;
  continents: GameContinent[];
  /** 已存在的 map_id，用于校验编辑重命名和创建镜像图时避免误覆盖。 */
  existingMapIDs?: string[];
  /** 当前 game 中与 initial.nested_group 同组的 maps（不含自己）。 */
  sameGroupMaps?: GameMapSummary[];
  submitting?: boolean;
  onSubmit: (input: MapEditorSubmitInput) => void;
  onCancel?: () => void;
  /**
   * "保存并继续新建"：提供时按钮显示在保存旁，点击会调用此回调而非 onSubmit。
   * 父组件在 mutation 成功后保持 Dialog 不关闭、表单状态全部保留，用户改 map_id /
   * name / match 等少量字段即可继续新建下一张图（同 NPC 多图、切割图等场景）。
   */
  onSubmitContinue?: (input: MapEditorSubmitInput) => void;
}

export type MapEditorSubmitInput = MapUpsertInput | MapUpsertInput[];

interface JSONFieldState {
  text: string;
  error: string | null;
}

function jsonFieldFrom(value: unknown, fallback: string): JSONFieldState {
  let initial = fallback;
  if (value !== undefined && value !== null) {
    try {
      initial = JSON.stringify(value, null, 2);
    } catch {
      initial = fallback;
    }
  }
  return { text: initial, error: null };
}

function parseJSONFieldOrError(state: JSONFieldState): { value: unknown; error: string | null } {
  try {
    return { value: state.text.trim() ? JSON.parse(state.text) : null, error: null };
  } catch (e) {
    return { value: null, error: (e as Error).message };
  }
}

function safeParseConditions(value: unknown): Condition[] {
  if (!Array.isArray(value)) return [];
  const out: Condition[] = [];
  for (const item of value) {
    const parsed = ConditionSchema.safeParse(item);
    if (parsed.success) out.push(parsed.data);
  }
  return out;
}

const BasicSchema = z.object({
  map_id: z.string().trim().min(1, "map_id 必填"),
  name: z.string().trim().min(1, "name 必填"),
  continent_id: z.string().trim().min(1, "选择大陆"),
});

export function MapEditor({
  initial,
  continents,
  existingMapIDs = [],
  sameGroupMaps = [],
  submitting,
  onSubmit,
  onCancel,
  onSubmitContinue,
}: MapEditorProps) {
  const isCreateMode = !initial.map_id;
  const [mapID, setMapID] = useState(initial.map_id ?? "");
  const [name, setName] = useState(initial.name ?? "");
  const [continentID, setContinentID] = useState(
    initial.continent_id ?? continents[0]?.continent_id ?? "",
  );
  const [nestedGroup, setNestedGroup] = useState(initial.nested_group ?? "");
  const [respawnModel, setRespawnModel] = useState(
    initial.respawn_model ?? "periodic_fill_mixed",
  );
  const [sortOrder, setSortOrder] = useState(String(initial.sort_order ?? 0));
  const [selectableDefault, setSelectableDefault] = useState(
    initial.selectable_default ?? true,
  );
  const [allowTransitWhenCooldown, setAllowTransitWhenCooldown] = useState(
    initial.allow_transit_when_cooldown ?? false,
  );
  const [mirrorEnabled, setMirrorEnabled] = useState(false);
  const [mirrorMapID, setMirrorMapID] = useState(deriveMirrorMapID(initial.map_id ?? ""));
  const [mirrorName, setMirrorName] = useState(deriveMirrorName(initial.name ?? ""));
  const [mirrorNpcName, setMirrorNpcName] = useState(
    deriveMirrorNpcName(deriveMirrorName(initial.name ?? "")),
  );
  const [mirrorNpcX, setMirrorNpcX] = useState("20");
  const [mirrorNpcY, setMirrorNpcY] = useState("23");
  const [mirrorNpcRange, setMirrorNpcRange] = useState("12");
  const [mirrorNpcTalkWaitTimeout, setMirrorNpcTalkWaitTimeout] = useState("1");
  const [mirrorMapWaitTimeout, setMirrorMapWaitTimeout] = useState("12");
  const [mirrorMapIDTouched, setMirrorMapIDTouched] = useState(false);
  const [mirrorNameTouched, setMirrorNameTouched] = useState(false);
  const [mirrorNpcNameTouched, setMirrorNpcNameTouched] = useState(false);

  const [conditions, setConditions] = useState<Condition[]>(
    safeParseConditions(initial.enterConditions),
  );
  const [enterJSON, setEnterJSON] = useState<JSONFieldState>(
    jsonFieldFrom(initial.enter, "[]"),
  );
  const [edgesJSON, setEdgesJSON] = useState<JSONFieldState>(
    jsonFieldFrom(initial.edges, "[]"),
  );
  const [matchJSON, setMatchJSON] = useState<JSONFieldState>(
    jsonFieldFrom(initial.match, "[]"),
  );
  const [fightJSON, setFightJSON] = useState<JSONFieldState>(
    jsonFieldFrom(initial.fight, "{}"),
  );
  const [bossNamesJSON, setBossNamesJSON] = useState<JSONFieldState>(
    jsonFieldFrom(initial.boss_names, "[]"),
  );

  useEffect(() => {
    if (!mirrorMapIDTouched) setMirrorMapID(deriveMirrorMapID(mapID));
  }, [mapID, mirrorMapIDTouched]);

  useEffect(() => {
    if (!mirrorNameTouched) setMirrorName(deriveMirrorName(name));
  }, [name, mirrorNameTouched]);

  useEffect(() => {
    if (!mirrorNpcNameTouched) setMirrorNpcName(deriveMirrorNpcName(mirrorName));
  }, [mirrorName, mirrorNpcNameTouched]);

  const initialMapID = initial.map_id?.trim() ?? "";

  const basicErrors = useMemo(() => {
    const nextMapID = mapID.trim();
    const parsed = BasicSchema.safeParse({
      map_id: mapID,
      name,
      continent_id: continentID,
    });
    const out: Record<string, string> = {};
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        const k = issue.path[0];
        if (typeof k === "string") out[k] = issue.message;
      }
    }
    if (
      nextMapID &&
      nextMapID !== initialMapID &&
      existingMapIDs.some((id) => id.trim() === nextMapID)
    ) {
      out.map_id = "map_id 已存在";
    }
    return out;
  }, [continentID, existingMapIDs, initialMapID, mapID, name]);

  const mirrorErrors = useMemo(() => {
    const out: Record<string, string> = {};
    if (!isCreateMode || !mirrorEnabled) return out;

    const primaryID = mapID.trim();
    const nextMirrorID = mirrorMapID.trim();
    const existing = new Set(existingMapIDs);
    if (!nextMirrorID) {
      out.mirror_map_id = "镜像 map_id 必填";
    } else if (nextMirrorID === primaryID) {
      out.mirror_map_id = "镜像 map_id 不能和主图相同";
    } else if (existing.has(nextMirrorID)) {
      out.mirror_map_id = "镜像 map_id 已存在";
    }
    if (!mirrorName.trim()) out.mirror_name = "镜像 name 必填";
    if (!isFiniteNumberText(mirrorNpcX)) out.mirror_npc_x = "X 必须是数字";
    if (!isFiniteNumberText(mirrorNpcY)) out.mirror_npc_y = "Y 必须是数字";
    if (!isFiniteNumberText(mirrorNpcRange)) out.mirror_npc_range = "范围必须是数字";
    if (!isFiniteNumberText(mirrorNpcTalkWaitTimeout)) {
      out.mirror_npc_talk_wait_timeout = "等待必须是数字";
    }
    if (!isFiniteNumberText(mirrorMapWaitTimeout)) {
      out.mirror_map_wait_timeout = "切图等待必须是数字";
    }
    return out;
  }, [
    existingMapIDs,
    isCreateMode,
    mapID,
    mirrorEnabled,
    mirrorMapID,
    mirrorMapWaitTimeout,
    mirrorName,
    mirrorNpcRange,
    mirrorNpcTalkWaitTimeout,
    mirrorNpcX,
    mirrorNpcY,
  ]);

  const jsonStates = { enterJSON, edgesJSON, matchJSON, fightJSON, bossNamesJSON };
  const anyJSONError = Object.values(jsonStates).some((s) => s.error);
  const canSubmit =
    !submitting &&
    Object.keys(basicErrors).length === 0 &&
    Object.keys(mirrorErrors).length === 0 &&
    !anyJSONError;

  function buildInput(): MapUpsertInput | null {
    const enter = parseJSONFieldOrError(enterJSON);
    const edges = parseJSONFieldOrError(edgesJSON);
    const match = parseJSONFieldOrError(matchJSON);
    const fight = parseJSONFieldOrError(fightJSON);
    const bossNames = parseJSONFieldOrError(bossNamesJSON);
    if (enter.error || edges.error || match.error || fight.error || bossNames.error) return null;
    return {
      id: initial.id,
      map_id: mapID,
      name,
      continent_id: continentID,
      nested_group: nestedGroup,
      respawn_model: respawnModel,
      boss_names: bossNames.value ?? [],
      sort_order: Number(sortOrder) || 0,
      selectable_default: selectableDefault,
      allow_transit_when_cooldown: allowTransitWhenCooldown,
      enter: enter.value ?? [],
      edges: edges.value ?? [],
      match: match.value ?? [],
      fight: fight.value ?? {},
      enterConditions: conditions,
      version: initial.version,
    };
  }

  function buildSubmitInput(): MapEditorSubmitInput | null {
    const input = buildInput();
    if (!input) return null;
    if (!isCreateMode || !mirrorEnabled) return input;
    return buildMirrorUpserts(input, {
      mirrorMapID: mirrorMapID.trim(),
      mirrorName: mirrorName.trim(),
      mirrorNpcName: mirrorNpcName.trim(),
      mirrorNpcX,
      mirrorNpcY,
      mirrorNpcRange,
      mirrorNpcTalkWaitTimeout,
      mirrorMapWaitTimeout,
    });
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!canSubmit) return;
    const input = buildSubmitInput();
    if (input) onSubmit(input);
  }

  function handleSubmitContinue() {
    if (!canSubmit || !onSubmitContinue) return;
    const input = buildSubmitInput();
    if (input) onSubmitContinue(input);
  }

  return (
    <form className="space-y-4" onSubmit={handleSubmit} noValidate>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Field label="map_id" htmlFor="map_id" error={basicErrors.map_id}>
          <Input
            id="map_id"
            value={mapID}
            onChange={(e) => setMapID(e.target.value)}
          />
        </Field>
        <Field label="name" htmlFor="name" error={basicErrors.name}>
          <Input
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
        <Field
          label="continent_id"
          htmlFor="continent_id"
          error={basicErrors.continent_id}
        >
          <select
            id="continent_id"
            value={continentID}
            onChange={(e) => setContinentID(e.target.value)}
            className="h-8 w-full rounded border border-[hsl(var(--input))] bg-transparent px-2 text-sm"
          >
            {continents.length === 0 ? <option value="">（无大陆）</option> : null}
            {continents.map((c) => (
              <option key={c.continent_id} value={c.continent_id}>
                {c.name || c.continent_id}
              </option>
            ))}
          </select>
        </Field>
        <Field label="nested_group" htmlFor="nested_group" hint="同组地图会在拓扑同色高亮">
          <Input
            id="nested_group"
            value={nestedGroup}
            onChange={(e) => setNestedGroup(e.target.value)}
          />
        </Field>
        <Field label="respawn_model" htmlFor="respawn_model">
          <select
            id="respawn_model"
            value={respawnModel}
            onChange={(e) => setRespawnModel(e.target.value)}
            className="h-8 w-full rounded border border-[hsl(var(--input))] bg-transparent px-2 text-sm"
          >
            <option value="periodic_fill_mixed">周期补刷混合</option>
            <option value="death_cooldown">死亡冷却</option>
            <option value="mixed_boss">同图 Boss 混合</option>
          </select>
        </Field>
        <Field label="sort_order" htmlFor="sort_order">
          <Input
            id="sort_order"
            type="number"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
          />
        </Field>
        <div className="flex items-end gap-4">
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={selectableDefault}
              onChange={(e) => setSelectableDefault(e.target.checked)}
            />
            默认可挂
          </label>
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={allowTransitWhenCooldown}
              onChange={(e) => setAllowTransitWhenCooldown(e.target.checked)}
            />
            冷却期允许过路
          </label>
        </div>
      </div>

      {isCreateMode ? (
        <div className="rounded border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 p-3">
          <label className="flex items-center gap-2 text-xs font-medium">
            <input
              type="checkbox"
              checked={mirrorEnabled}
              onChange={(e) => setMirrorEnabled(e.target.checked)}
            />
            同时创建镜像地图
          </label>
          {mirrorEnabled ? (
            <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-3">
              <Field
                label="镜像 map_id"
                htmlFor="mirror_map_id"
                error={mirrorErrors.mirror_map_id}
              >
                <Input
                  id="mirror_map_id"
                  value={mirrorMapID}
                  onChange={(e) => {
                    setMirrorMapIDTouched(true);
                    setMirrorMapID(e.target.value);
                  }}
                />
              </Field>
              <Field
                label="镜像 name"
                htmlFor="mirror_name"
                error={mirrorErrors.mirror_name}
              >
                <Input
                  id="mirror_name"
                  value={mirrorName}
                  onChange={(e) => {
                    setMirrorNameTouched(true);
                    setMirrorName(e.target.value);
                  }}
                />
              </Field>
              <Field label="镜像 NPC" htmlFor="mirror_npc_name">
                <Input
                  id="mirror_npc_name"
                  value={mirrorNpcName}
                  onChange={(e) => {
                    setMirrorNpcNameTouched(true);
                    setMirrorNpcName(e.target.value);
                  }}
                />
              </Field>
              <Field
                label="NPC X"
                htmlFor="mirror_npc_x"
                error={mirrorErrors.mirror_npc_x}
              >
                <Input
                  id="mirror_npc_x"
                  type="number"
                  value={mirrorNpcX}
                  onChange={(e) => setMirrorNpcX(e.target.value)}
                />
              </Field>
              <Field
                label="NPC Y"
                htmlFor="mirror_npc_y"
                error={mirrorErrors.mirror_npc_y}
              >
                <Input
                  id="mirror_npc_y"
                  type="number"
                  value={mirrorNpcY}
                  onChange={(e) => setMirrorNpcY(e.target.value)}
                />
              </Field>
              <Field
                label="NPC 范围"
                htmlFor="mirror_npc_range"
                error={mirrorErrors.mirror_npc_range}
              >
                <Input
                  id="mirror_npc_range"
                  type="number"
                  value={mirrorNpcRange}
                  onChange={(e) => setMirrorNpcRange(e.target.value)}
                />
              </Field>
              <Field
                label="NPC 对话等待"
                htmlFor="mirror_npc_talk_wait_timeout"
                error={mirrorErrors.mirror_npc_talk_wait_timeout}
              >
                <Input
                  id="mirror_npc_talk_wait_timeout"
                  type="number"
                  value={mirrorNpcTalkWaitTimeout}
                  onChange={(e) => setMirrorNpcTalkWaitTimeout(e.target.value)}
                />
              </Field>
              <Field
                label="切图等待"
                htmlFor="mirror_map_wait_timeout"
                error={mirrorErrors.mirror_map_wait_timeout}
              >
                <Input
                  id="mirror_map_wait_timeout"
                  type="number"
                  value={mirrorMapWaitTimeout}
                  onChange={(e) => setMirrorMapWaitTimeout(e.target.value)}
                />
              </Field>
            </div>
          ) : null}
        </div>
      ) : null}

      {sameGroupMaps.length > 0 ? (
        <div className="rounded border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/40 p-3 text-xs">
          <div className="mb-1 font-medium">同组 ({nestedGroup})</div>
          <div className="flex flex-wrap gap-1">
            {sameGroupMaps.map((m) => (
              <StatusBadge key={m.map_id} variant="neutral">
                {m.name || m.map_id}
              </StatusBadge>
            ))}
          </div>
        </div>
      ) : null}

      <div className="space-y-2">
        <h4 className="text-xs font-semibold">enterConditions</h4>
        <ConditionBuilder value={conditions} onChange={setConditions} />
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <JSONField
          label="enter (steps JSON)"
          state={enterJSON}
          onChange={(s) =>
            setEnterJSON({ text: s, error: validateJSON(s) })
          }
        />
        <JSONField
          label="edges JSON"
          state={edgesJSON}
          onChange={(s) =>
            setEdgesJSON({ text: s, error: validateJSON(s) })
          }
        />
        <JSONField
          label="match JSON"
          state={matchJSON}
          onChange={(s) =>
            setMatchJSON({ text: s, error: validateJSON(s) })
          }
        />
        <JSONField
          label="fight_defaults JSON"
          state={fightJSON}
          onChange={(s) =>
            setFightJSON({ text: s, error: validateJSON(s) })
          }
        />
        <JSONField
          label="boss_names JSON"
          state={bossNamesJSON}
          onChange={(s) =>
            setBossNamesJSON({ text: s, error: validateJSON(s) })
          }
        />
      </div>

      <div className="flex items-center justify-end gap-2 pt-2">
        {onCancel ? (
          <Button type="button" variant="outline" size="sm" onClick={onCancel}>
            取消
          </Button>
        ) : null}
        {onSubmitContinue ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!canSubmit}
            onClick={handleSubmitContinue}
          >
            {submitting ? "保存中…" : "保存并继续新建"}
          </Button>
        ) : null}
        <Button type="submit" size="sm" disabled={!canSubmit}>
          {submitting ? "保存中…" : "保存"}
        </Button>
      </div>
    </form>
  );
}

function validateJSON(text: string): string | null {
  const t = text.trim();
  if (!t) return null;
  try {
    JSON.parse(t);
    return null;
  } catch (e) {
    return (e as Error).message;
  }
}

interface MirrorFormValues {
  mirrorMapID: string;
  mirrorName: string;
  mirrorNpcName: string;
  mirrorNpcX: string;
  mirrorNpcY: string;
  mirrorNpcRange: string;
  mirrorNpcTalkWaitTimeout: string;
  mirrorMapWaitTimeout: string;
}

function buildMirrorUpserts(
  primary: MapUpsertInput,
  mirror: MirrorFormValues,
): MapUpsertInput[] {
  const primaryMapID = primary.map_id.trim();
  const mirrorMapID = mirror.mirrorMapID.trim();
  const mirrorName = mirror.mirrorName.trim();
  const group = (primary.nested_group ?? "").trim() || deriveNestedGroup(primaryMapID);
  const edge = buildMirrorEdge(mirrorMapID, mirrorName, mirror);
  return [
    {
      ...primary,
      nested_group: group,
      edges: appendMirrorEdge(primary.edges, edge, mirrorMapID),
    },
    {
      map_id: mirrorMapID,
      name: mirrorName,
      continent_id: primary.continent_id,
      nested_group: group,
      sort_order: (primary.sort_order ?? 0) + 1,
      respawn_model: primary.respawn_model,
      boss_names: primary.boss_names ?? [],
      selectable_default: false,
      allow_transit_when_cooldown: false,
      enter: [],
      edges: [],
      match: [mirrorName],
      fight: primary.fight ?? {},
      enterConditions: primary.enterConditions ?? [],
    },
  ];
}

function buildMirrorEdge(
  mirrorMapID: string,
  mirrorName: string,
  values: MirrorFormValues,
) {
  return {
    to: mirrorMapID,
    type: "talkNpc",
    npcName: values.mirrorNpcName.trim() || deriveMirrorNpcName(mirrorName),
    waitMap: [mirrorName],
    npcTalkWaitTimeout: numberFromText(values.mirrorNpcTalkWaitTimeout, 1),
    mapWaitTimeout: numberFromText(values.mirrorMapWaitTimeout, 12),
    npcX: numberFromText(values.mirrorNpcX, 20),
    npcY: numberFromText(values.mirrorNpcY, 23),
    npcRange: numberFromText(values.mirrorNpcRange, 12),
  };
}

function appendMirrorEdge(
  edges: unknown,
  edge: Record<string, unknown>,
  mirrorMapID: string,
) {
  const next = Array.isArray(edges) ? [...edges] : [];
  const exists = next.some((item) => {
    if (!item || typeof item !== "object") return false;
    const target = (item as { to?: unknown; target?: unknown; map_id?: unknown }).to ??
      (item as { target?: unknown }).target ??
      (item as { map_id?: unknown }).map_id;
    return target === mirrorMapID;
  });
  if (!exists) next.push(edge);
  return next;
}

function deriveMirrorMapID(mapID: string) {
  const id = mapID.trim();
  if (!id) return "";
  const floorMatch = id.match(/^(.*)_floor_([^_]+)$/);
  if (floorMatch) return `${floorMatch[1]}_mirror_${floorMatch[2]}`;
  if (id.endsWith("_main")) return `${id.slice(0, -5)}_mirror`;
  if (id.includes("floor")) return id.replace("floor", "mirror");
  return `${id}_mirror`;
}

function deriveMirrorName(name: string) {
  const text = name.trim();
  if (!text) return "";
  return text.endsWith("镜像") ? text : `${text}镜像`;
}

function deriveMirrorNpcName(name: string) {
  const text = name.trim();
  if (!text) return "";
  return `【${text}】`;
}

function deriveNestedGroup(mapID: string) {
  const id = mapID.trim();
  return id ? `${id}_nested` : "";
}

function isFiniteNumberText(value: string) {
  return Number.isFinite(Number(value));
}

function numberFromText(value: string, fallback: number) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function JSONField({
  label,
  state,
  onChange,
}: {
  label: string;
  state: JSONFieldState;
  onChange: (text: string) => void;
}) {
  return (
    <Field label={label} error={state.error ?? undefined}>
      <Textarea
        rows={6}
        value={state.text}
        onChange={(e) => onChange(e.target.value)}
        spellCheck={false}
      />
    </Field>
  );
}
